import { ReactNode, useEffect, useRef, useState, ElementType, CSSProperties } from "react";
import { cn } from "@/lib/utils";

type RevealVariant = "fade-up" | "fade-in" | "scale-in" | "slide-left" | "slide-right";

interface RevealProps {
  children: ReactNode;
  as?: ElementType;
  variant?: RevealVariant;
  delay?: number;
  duration?: number;
  className?: string;
  threshold?: number;
  once?: boolean;
}

const variantClasses: Record<RevealVariant, { hidden: string; shown: string }> = {
  "fade-up": {
    hidden: "opacity-0 translate-y-6",
    shown: "opacity-100 translate-y-0",
  },
  "fade-in": {
    hidden: "opacity-0",
    shown: "opacity-100",
  },
  "scale-in": {
    hidden: "opacity-0 scale-95",
    shown: "opacity-100 scale-100",
  },
  "slide-left": {
    hidden: "opacity-0 -translate-x-8",
    shown: "opacity-100 translate-x-0",
  },
  "slide-right": {
    hidden: "opacity-0 translate-x-8",
    shown: "opacity-100 translate-x-0",
  },
};

const Reveal = ({
  children,
  as: Tag = "div",
  variant = "fade-up",
  delay = 0,
  duration = 700,
  className,
  threshold = 0.15,
  once = true,
}: RevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once]);

  const v = variantClasses[variant];
  const style: CSSProperties = {
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
  };

  return (
    <Tag
      ref={ref as React.Ref<HTMLElement>}
      style={style}
      className={cn(
        "transition-all ease-out will-change-transform",
        visible ? v.shown : v.hidden,
        className
      )}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
